import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Plus, Search, CalendarDays, ExternalLink, RefreshCw, X,
  AlertCircle, Loader2, Check,
} from 'lucide-react';
import SocialPostComposer from '@/components/social/SocialPostComposer';
import SocialCalendarPostCard from '@/components/social/SocialCalendarPostCard';
import { toastPublishOutcome } from '@/lib/socialPublishOutcome';

const STATUS_LABELS = {
  draft: 'Borrador', scheduled: 'Programada', publishing: 'Publicando...',
  published: 'Publicada', partial: 'Parcial', failed: 'Falló',
};
const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 7am–10pm
const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const NETWORK_LABELS = { facebook: 'Facebook', instagram: 'Instagram' };

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function heatColor(score) {
  const alpha = 0.06 + score * 0.28; // sutil — nunca compite con las tarjetas
  return `rgba(247, 37, 133, ${alpha.toFixed(2)})`;
}

// ── Popover con lista completa de una celda ────────────────────────────
function CellPostsPopover({ posts, onOpenPost }) {
  return (
    <div className="space-y-1 max-h-64 overflow-y-auto">
      {posts.map((p) => (
        <SocialCalendarPostCard key={p.id} post={p} onClick={onOpenPost} />
      ))}
    </div>
  );
}

// ── Panel de detalle (publishing / partial / published multi-red) ─────
function PostDetailDialog({ post, open, onClose, onEdit, onRetry, onOpenPermalink }) {
  if (!post) return null;
  const entries = Object.entries(post.results || {});
  const publishedNetworks = entries.filter(([, r]) => r.status === 'published');
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Detalle de publicación</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{post.content_text || '(sin texto)'}</p>
          <div className="space-y-1.5">
            {entries.map(([net, r]) => (
              <div key={net} className="flex items-center justify-between text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-1.5">
                <span className="font-medium text-[#0F172A]">{NETWORK_LABELS[net] || net}</span>
                <span className={r.status === 'published' ? 'text-emerald-600' : r.status === 'not_connected' || r.status === 'unsupported' ? 'text-[#94A3B8]' : 'text-red-500'}>
                  {r.status}{r.error ? ` — ${r.error.slice(0, 80)}` : ''}
                </span>
                {r.status === 'published' && r.permalink_url && (
                  <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => onOpenPermalink(r.permalink_url)}>
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {post.status === 'publishing' && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Publicando ahora mismo — este panel es de solo lectura.</p>
          )}
        </div>
        <DialogFooter className="gap-2">
          {publishedNetworks.length === 1 && publishedNetworks[0][1].permalink_url && (
            <Button variant="outline" onClick={() => onOpenPermalink(publishedNetworks[0][1].permalink_url)} className="gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> Ver publicación
            </Button>
          )}
          {(post.status === 'failed' || post.status === 'partial') && (
            <Button variant="outline" onClick={() => onRetry(post)} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Reintentar red fallida</Button>
          )}
          {(post.status === 'failed' || post.status === 'partial') && (
            <Button onClick={() => onEdit(post)}>Editar</Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SocialPublishing() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heatmap, setHeatmap] = useState([]);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // week | month | list
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerSlot, setComposerSlot] = useState(null);
  const [composerPost, setComposerPost] = useState(null);
  const [detailPost, setDetailPost] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [timezone, setTimezone] = useState(null);

  const range = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(anchorDate);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      return { from: start, to: end };
    }
    if (viewMode === 'month') {
      const start = startOfWeek(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));
      const end = new Date(start); end.setDate(end.getDate() + 42);
      return { from: start, to: end };
    }
    // list: rango amplio (90 días atrás / adelante) — la búsqueda/filtros acotan
    const start = new Date(); start.setDate(start.getDate() - 90);
    const end = new Date(); end.setDate(end.getDate() + 365);
    return { from: start, to: end };
  }, [viewMode, anchorDate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: range.from.toISOString(), to: range.to.toISOString(), page_size: 500 };
      if (statusFilter) params.status = statusFilter;
      if (networkFilter) params.network = networkFilter;
      if (search.trim()) params.search = search.trim();
      const [{ data: postData }, { data: heatData }] = await Promise.all([
        api.get('/tenant/social-posts', { params }),
        api.get('/tenant/social-posts/heatmap'),
      ]);
      setPosts(postData.posts || []);
      setTimezone(postData.timezone || null);
      setHeatmap(heatData.cells || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [range, statusFilter, networkFilter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const heatByCell = useMemo(() => {
    const map = {};
    heatmap.forEach((c) => { map[`${c.day}-${c.hour}`] = c.score; });
    return map;
  }, [heatmap]);

  const postsByDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const at = p.scheduled_at || p.published_at || p.created_at;
      if (!at) continue;
      const d = new Date(at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push({ ...p, _date: d });
    }
    for (const key in map) map[key].sort((a, b) => a._date - b._date);
    return map;
  }, [posts]);

  const openComposerAt = (date) => { setComposerSlot(date); setComposerPost(null); setComposerOpen(true); };

  const handleOpenPost = (post) => {
    if (post.status === 'draft' || post.status === 'scheduled' || post.status === 'failed') {
      setComposerPost(post); setComposerSlot(null); setComposerOpen(true);
      return;
    }
    if (post.status === 'publishing' || post.status === 'partial') {
      setDetailPost(post);
      return;
    }
    if (post.status === 'published') {
      const entries = Object.entries(post.results || {}).filter(([, r]) => r.status === 'published');
      if (entries.length === 1 && entries[0][1].permalink_url) {
        window.open(entries[0][1].permalink_url, '_blank', 'noopener,noreferrer');
      } else {
        setDetailPost(post);
      }
      return;
    }
    setDetailPost(post);
  };

  const retryPost = async (post) => {
    try {
      const { data } = await api.post(`/tenant/social-posts/${post.id}/publish-now`);
      toastPublishOutcome(toast, data);
      setDetailPost(null);
      await fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading && posts.length === 0) return <div className="p-8 text-center text-sm text-[#64748B]">Cargando...</div>;

  const weekStart = startOfWeek(anchorDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const monthGridStart = startOfWeek(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));
  const monthDays = Array.from({ length: 42 }, (_, i) => { const d = new Date(monthGridStart); d.setDate(d.getDate() + i); return d; });

  const listPosts = [...posts].sort((a, b) => new Date(b.scheduled_at || b.published_at || b.created_at) - new Date(a.scheduled_at || a.published_at || a.created_at));

  return (
    <div className="space-y-4" data-testid="social-publishing">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold text-[#0F172A]">Calendario de publicaciones</h2>
          <p className="text-sm text-[#64748B]">
            Facebook e Instagram publican de verdad hoy — el resto se activa conforme conectemos cada red.
            {timezone && <span className="text-[#94A3B8]"> · Zona horaria: {timezone}</span>}
          </p>
        </div>
        <Button onClick={() => openComposerAt(new Date())} className="gap-2"><Plus className="w-4 h-4" /> Nueva publicación</Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-1 bg-[#F1F5F9] rounded-lg p-0.5">
            {['week', 'month', 'list'].map((v) => (
              <button key={v} onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${viewMode === v ? 'bg-white shadow-sm text-[#0F172A]' : 'text-[#64748B]'}`}>
                {v === 'week' ? 'Semana' : v === 'month' ? 'Mes' : 'Lista'}
              </button>
            ))}
          </div>
          {viewMode !== 'list' && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setAnchorDate(new Date())}><CalendarDays className="w-3.5 h-3.5 mr-1" /> Hoy</Button>
              <Button size="sm" variant="outline" onClick={() => { const d = new Date(anchorDate); viewMode === 'week' ? d.setDate(d.getDate() - 7) : d.setMonth(d.getMonth() - 1); setAnchorDate(d); }}><ChevronLeft className="w-3.5 h-3.5" /></Button>
              <span className="text-sm font-medium text-[#0F172A] min-w-[140px] text-center">
                {viewMode === 'week'
                  ? `${weekStart.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${weekDays[6].toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
                  : anchorDate.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
              </span>
              <Button size="sm" variant="outline" onClick={() => { const d = new Date(anchorDate); viewMode === 'week' ? d.setDate(d.getDate() + 7) : d.setMonth(d.getMonth() + 1); setAnchorDate(d); }}><ChevronRight className="w-3.5 h-3.5" /></Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#94A3B8]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en el texto..." className="pl-8 h-8 text-xs" data-testid="social-search" />
          </div>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-36"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={networkFilter || 'all'} onValueChange={(v) => setNetworkFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-36"><SelectValue placeholder="Red" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las redes</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
            </SelectContent>
          </Select>
          {viewMode !== 'list' && (
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[11px] text-[#64748B]">Mejores horarios</span>
              <Switch checked={showHeatmap} onCheckedChange={setShowHeatmap} />
            </div>
          )}
        </div>

        {viewMode !== 'list' && showHeatmap && (
          <div className="flex items-center gap-1.5 text-[10px] text-[#94A3B8]">
            <span>Menos recomendado</span>
            <span className="flex gap-0.5">{[0.1, 0.3, 0.5, 0.7, 0.9].map((s) => <span key={s} className="w-4 h-2.5 rounded-sm" style={{ backgroundColor: heatColor(s) }} />)}</span>
            <span>Más recomendado</span>
            <span className="ml-2">— guía general de la industria, no un análisis de tus propias publicaciones todavía.</span>
          </div>
        )}

        {viewMode === 'week' && (
          <div className="overflow-x-auto">
            <div className="grid grid-cols-8 min-w-[900px]" data-testid="social-calendar-week">
              <div />
              {weekDays.map((d, i) => (
                <div key={i} className="text-center text-xs font-medium text-[#64748B] pb-2">{DAY_LABELS[i]} <span className="text-[#CBD5E1]">{d.getDate()}</span></div>
              ))}
              {HOURS.map((hour) => (
                <Fragment key={hour}>
                  <div className="text-[10px] text-[#94A3B8] text-right pr-2 py-1">{hour}:00</div>
                  {weekDays.map((d, dayIdx) => {
                    const score = heatByCell[`${dayIdx}-${hour}`] || 0.2;
                    const cellDate = new Date(d); cellDate.setHours(hour, 0, 0, 0);
                    const key = `${cellDate.getFullYear()}-${cellDate.getMonth()}-${cellDate.getDate()}`;
                    const dayPosts = (postsByDay[key] || []).filter((p) => p._date.getHours() === hour);
                    return (
                      <div key={`${dayIdx}-${hour}`} onClick={() => dayPosts.length === 0 && openComposerAt(cellDate)}
                        data-testid={`heat-cell-${dayIdx}-${hour}`}
                        style={{ backgroundColor: showHeatmap ? heatColor(score) : 'transparent' }}
                        className="relative min-h-[40px] border border-white p-0.5 space-y-0.5 hover:ring-1 hover:ring-[#F72585] transition-all cursor-pointer">
                        {dayPosts.slice(0, 2).map((p) => <SocialCalendarPostCard key={p.id} post={p} onClick={handleOpenPost} compact />)}
                        {dayPosts.length > 2 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" onClick={(e) => e.stopPropagation()} className="text-[9px] text-[#F72585] font-medium px-1">+{dayPosts.length - 2} más</button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64" onClick={(e) => e.stopPropagation()}>
                              <CellPostsPopover posts={dayPosts} onOpenPost={handleOpenPost} />
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'month' && (
          <div className="grid grid-cols-7 gap-1" data-testid="social-calendar-month">
            {DAY_LABELS.map((d) => <div key={d} className="text-center text-xs font-medium text-[#64748B] pb-1">{d}</div>)}
            {monthDays.map((d, i) => {
              const inMonth = d.getMonth() === anchorDate.getMonth();
              const dayIdx = (d.getDay() + 6) % 7;
              const peakScore = Math.max(...HOURS.map((h) => heatByCell[`${dayIdx}-${h}`] || 0.2));
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayPosts = postsByDay[key] || [];
              return (
                <div key={i}
                  onClick={() => { if (!inMonth) return; if (dayPosts.length === 0) { const dt = new Date(d); dt.setHours(9, 0, 0, 0); openComposerAt(dt); } }}
                  data-testid={`month-cell-${i}`}
                  style={{ backgroundColor: inMonth && showHeatmap ? heatColor(peakScore) : 'transparent' }}
                  className={`min-h-[76px] rounded-lg border border-white p-1 space-y-0.5 ${inMonth ? 'hover:ring-1 hover:ring-[#F72585] cursor-pointer' : 'opacity-30'}`}>
                  <span className="text-xs font-medium text-[#0F172A]">{d.getDate()}</span>
                  {dayPosts.slice(0, 2).map((p) => <SocialCalendarPostCard key={p.id} post={p} onClick={handleOpenPost} compact />)}
                  {dayPosts.length > 2 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" onClick={(e) => e.stopPropagation()} className="text-[9px] text-[#F72585] font-medium">+{dayPosts.length - 2} más</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64" onClick={(e) => e.stopPropagation()}>
                        <CellPostsPopover posts={dayPosts} onOpenPost={handleOpenPost} />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {viewMode === 'list' && (
          <div className="space-y-1.5" data-testid="social-calendar-list">
            {listPosts.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-8">Sin publicaciones para estos filtros</p>
            ) : listPosts.map((p) => <SocialCalendarPostCard key={p.id} post={p} onClick={handleOpenPost} />)}
          </div>
        )}
      </div>

      <SocialPostComposer
        open={composerOpen}
        onClose={() => { setComposerOpen(false); setComposerPost(null); }}
        onCreated={fetchAll}
        initialDateTime={composerSlot}
        post={composerPost}
      />
      <PostDetailDialog
        post={detailPost}
        open={!!detailPost}
        onClose={() => setDetailPost(null)}
        onEdit={(p) => { setDetailPost(null); setComposerPost(p); setComposerSlot(null); setComposerOpen(true); }}
        onRetry={retryPost}
        onOpenPermalink={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
      />
    </div>
  );
}
